// Web view model for canonical nakshatra facts plus presentation-only copy.

import { nakshatras, type Nakshatra, type NakshatraId } from "@ishvaram/domain-data/nakshatras";
import { planetMap, planets, type Planet } from "@ishvaram/domain-data/planets";
import { rashiMap, rashis } from "@ishvaram/domain-data/rashis";

import { significationLabels } from "./nakshatra-signification-labels";

import {
  NAKSHATRA_PAGE_PRESENTATION,
  type NakshatraPagePresentation,
} from "./nakshatra-page-presentation-data";

export interface NakshatraData {
  id: NakshatraId;
  name_en: string;
  name_hi: string;
  number: number;
  degree_start: number;
  degree_end: number;
  rashi: string;
  rashi_en: string;
  ruler: string;
  deity: string;
  symbol: string;
  nature: string;
  gana: string;
  quality: string;
  signifies: string[];
  seo_text: string;
  padas: { navamsha: string; traits: string }[];
}

export interface PlanetData {
  id: string;
  name_en: string;
  name_hi: string;
  signifies: string[];
  gemstone: string;
  day: string;
  color: string;
}

const NATURE_LABELS: Readonly<Record<string, string>> = {
  deva: "Deva (Divine)",
  manushya: "Manushya (Human)",
  rakshasa: "Rakshasa (Fierce)",
};

const QUALITY_LABELS: Readonly<Record<string, string>> = {
  swift: "Swift (Kshipra)",
  fierce: "Fierce (Ugra)",
  mixed: "Mixed (Mishra)",
  fixed: "Fixed (Dhruva)",
  soft: "Soft (Mridu)",
  sharp: "Sharp (Tikshna)",
  movable: "Movable (Chara)",
  light: "Light (Laghu)",
};

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function normalizeVedicName(value: string): string {
  return value.toLowerCase().replace(/a$/, "");
}

const rashiByVedicName = new Map(rashis.map((rashi) => [normalizeVedicName(rashi.name_sa), rashi]));

function requirePresentation(id: NakshatraId): NakshatraPagePresentation {
  const presentation = NAKSHATRA_PAGE_PRESENTATION[id];
  if (!presentation || presentation.id !== id) {
    throw new Error(`Missing nakshatra page presentation for canonical id "${id}"`);
  }
  return presentation;
}

function requireLabel(labels: Readonly<Record<string, string>>, value: string): string {
  const label = labels[value];
  if (!label) throw new Error(`Missing presentation label for canonical value "${value}"`);
  return label;
}

function toPlanetData(planet: Planet): PlanetData {
  return {
    id: planet.id,
    name_en: planet.name_en,
    name_hi: planet.name_hi,
    signifies: significationLabels(planet.signifies),
    gemstone: titleCase(planet.gemstone),
    day: titleCase(planet.day),
    color: titleCase(planet.color),
  };
}

function toNakshatraData(canonical: Nakshatra): NakshatraData {
  const presentation = requirePresentation(canonical.id);
  const rashi = rashiMap[canonical.rashi];
  const ruler = planetMap[canonical.ruler];
  if (!rashi) throw new Error(`Unknown rashi "${canonical.rashi}" for ${canonical.id}`);
  if (!ruler) throw new Error(`Unknown ruler "${canonical.ruler}" for ${canonical.id}`);
  if (!canonical.padas || canonical.padas.length !== 4) {
    throw new Error(`Canonical nakshatra "${canonical.id}" must have exactly four padas`);
  }

  const padas = canonical.padas.map((pada, index) => {
    const navamsha = pada.navamsha_rashi
      ? rashiByVedicName.get(normalizeVedicName(pada.navamsha_rashi))
      : undefined;
    if (!navamsha) {
      throw new Error(`Unknown navamsha rashi for ${canonical.id} pada ${pada.pada}`);
    }
    return {
      navamsha: navamsha.name_en,
      traits: presentation.padaTraits[index],
    };
  });

  return {
    id: canonical.id,
    name_en: canonical.name_en,
    name_hi: canonical.name_hi,
    number: canonical.number,
    degree_start: canonical.degree_start,
    degree_end: canonical.degree_end,
    rashi: canonical.rashi,
    rashi_en: rashi.name_en,
    ruler: ruler.id,
    deity: canonical.deity,
    symbol: canonical.symbol,
    nature: requireLabel(NATURE_LABELS, canonical.nature),
    gana: titleCase(canonical.gana),
    quality: requireLabel(QUALITY_LABELS, canonical.quality),
    signifies: significationLabels(canonical.signifies),
    seo_text: presentation.seoText,
    padas,
  };
}

export const PLANETS: Readonly<Record<string, PlanetData>> = Object.fromEntries(
  planets.map((planet) => [planet.id, toPlanetData(planet)])
);

export const RASHI_NAMES: Readonly<Record<string, { en: string; hi: string }>> = Object.fromEntries(
  rashis.map((rashi) => [rashi.id, { en: rashi.name_en, hi: rashi.name_hi }])
);

const SIGNS_IN_ORDER = [...rashis].sort((left, right) => left.number - right.number);
const SIGN_MINUTES = 30 * 60;
const PADA_MINUTES = 200; // 3°20'

/**
 * A sidereal longitude as degrees and whole arc-minutes within its sign
 * ("13°20' Aries"). Rounds to the nearest arc-minute on the whole longitude, so
 * 13.3333…° reads 13°20' (flooring printed 13°19' on 18 of the 27 nakshatra
 * pages and their FAQ JSON-LD), and a minute that rounds to 60' carries into the
 * next degree and, at 30°, into the next sign.
 */
export function formatDegree(degree: number): string {
  const totalMinutes = Math.round(degree * 60);
  const wholeDegrees = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const sign = SIGNS_IN_ORDER[Math.floor(wholeDegrees / 30) % SIGNS_IN_ORDER.length];
  return `${wholeDegrees % 30}\u00b0${minutes}' ${sign.name_en}`;
}

/**
 * `formatDegree` for the END of a span. An end exactly on a sign cusp reads as
 * 30°0' of the sign it closes: Ashlesha, Jyeshtha and Revati end on one, and
 * "16°40' Cancer to 0°0' Leo" read as if Ashlesha crossed into Leo.
 */
export function formatDegreeEnd(degree: number): string {
  const totalMinutes = Math.round(degree * 60);
  if (totalMinutes <= 0 || totalMinutes % SIGN_MINUTES !== 0) return formatDegree(degree);
  const closes = SIGNS_IN_ORDER[(totalMinutes / SIGN_MINUTES - 1) % SIGNS_IN_ORDER.length];
  return `30\u00b00' ${closes.name_en}`;
}

/** One rashi a nakshatra's padas fall in, with those pada numbers (1-4). */
export interface NakshatraRashiSpan {
  rashi: string;
  en: string;
  hi: string;
  padas: number[];
}

/**
 * The rashis a nakshatra's four padas fall in, in order. A pada is 3°20', so
 * nine of the 27 cross a sign cusp (Krittika: pada 1 in Aries, padas 2-4 in
 * Taurus) and `rashi_en`, the rashi of its start, names only the first. The
 * other 18 return one rashi holding all four padas. Worked in whole
 * arc-minutes, so a pada that starts exactly on a cusp belongs to the next sign.
 */
export function nakshatraRashiSpans(n: Pick<NakshatraData, "degree_start">): NakshatraRashiSpan[] {
  const start = Math.round(n.degree_start * 60);
  const spans: NakshatraRashiSpan[] = [];
  for (let pada = 1; pada <= 4; pada++) {
    const at = start + (pada - 1) * PADA_MINUTES;
    const sign = SIGNS_IN_ORDER[Math.floor(at / SIGN_MINUTES) % SIGNS_IN_ORDER.length];
    const last = spans[spans.length - 1];
    if (last?.rashi === sign.id) last.padas.push(pada);
    else spans.push({ rashi: sign.id, en: sign.name_en, hi: sign.name_hi, padas: [pada] });
  }
  return spans;
}

type RashiLocale = "en" | "hi";

/** Degrees and whole arc-minutes within the sign, rounded as `formatDegree` rounds. */
function degreeWithinSign(degree: number): string {
  const totalMinutes = Math.round(degree * 60);
  return `${Math.floor(totalMinutes / 60) % 30}°${totalMinutes % 60}'`;
}

/**
 * Every rashi a nakshatra's span touches, by name, in order: "Aries" for
 * Ashwini, "Aries → Taurus" for Krittika.
 */
export function nakshatraRashiNames(
  n: Pick<NakshatraData, "degree_start">,
  locale: RashiLocale = "en"
): string {
  return nakshatraRashiSpans(n)
    .map((span) => (locale === "hi" ? span.hi : span.en))
    .join(" → ");
}

/**
 * The rashi a nakshatra sits in, or, for the nine that cross a sign cusp, both
 * rashis with the degree split: where the span starts in the first and where it
 * ends in the second ("Aries 26°40' → Taurus 10°0'" for Krittika). `rashi_en`
 * names only the sign the span starts in, so the hero line, the Rashi quick fact
 * and the meta description read this instead.
 */
export function nakshatraRashiLabel(
  n: Pick<NakshatraData, "degree_start" | "degree_end">,
  locale: RashiLocale = "en"
): string {
  const spans = nakshatraRashiSpans(n);
  const name = (span: NakshatraRashiSpan) => (locale === "hi" ? span.hi : span.en);
  if (spans.length === 1) return name(spans[0]);
  const first = spans[0];
  const last = spans[spans.length - 1];
  return (
    `${name(first)} ${degreeWithinSign(n.degree_start)} → ` +
    `${name(last)} ${degreeWithinSign(n.degree_end)}`
  );
}

export function ordinal(value: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = value % 100;
  return value + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]);
}

export function capitalize(value: string): string {
  return titleCase(value);
}

export const NAKSHATRA_DATA: NakshatraData[] = nakshatras.map(toNakshatraData);
